import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Calendar, Download } from "lucide-react";
import LoadingSpinner from "@/components/LoadingSpinner";

const MESES = [
  "Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];

function DiaCelula({ item, index, agendamentosDia, hoje, abrirDia }) {
  const temAgendamentos = agendamentosDia.length > 0;
  return (
    <div
      className={`
        min-h-[90px] p-2 border rounded-lg transition-all cursor-pointer
        ${item.dia ? "bg-white hover:bg-slate-50 hover:border-slate-300" : "bg-slate-50/50"}
        ${hoje ? "border-slate-900 border-2 shadow-sm" : "border-slate-200"}
      `}
      onClick={() => item.dia && abrirDia(item.data)}
      data-testid={item.data ? `dia-${item.data}` : `dia-vazio-${index}`}
    >
      {item.dia && (
        <>
          <div className="flex items-center justify-between mb-1">
            <span className={`text-sm font-medium ${hoje ? "bg-slate-900 text-white rounded-full w-7 h-7 flex items-center justify-center" : "text-slate-700"}`}>
              {item.dia}
            </span>
            {temAgendamentos && (
              <span className="text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded-full font-medium">
                {agendamentosDia.length}
              </span>
            )}
          </div>

          <div className="space-y-0.5 max-h-[50px] overflow-hidden">
            {agendamentosDia.slice(0, 2).map((ag) => (
              <div
                key={ag.id}
                className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded truncate"
                title={`${ag.horario} - ${ag.nome || ag.cliente || "Agendado"} (${ag.loja})`}
              >
                <span className="font-medium text-slate-700">{ag.horario}</span>
                <span className="ml-1 text-slate-500">{ag.loja?.substring(0, 3)}</span>
              </div>
            ))}
            {agendamentosDia.length > 2 && (
              <div className="text-[10px] text-slate-400 text-center">+{agendamentosDia.length - 2} mais</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function AgendaCalendar({
  mesAtual, anoAtual, diasDoMes, loading, navegarMes, exportarExcel,
  getAgendamentosDoDia, isHoje, abrirDia,
}) {
  return (
    <div className="space-y-6" data-testid="agenda-container">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Calendar className="w-8 h-8 text-slate-700" />
            Agenda
          </h1>
          <p className="text-sm text-slate-500 mt-1">Clique em um dia para ver os horários disponíveis</p>
        </div>

        <Button
          onClick={exportarExcel}
          variant="outline"
          className="flex items-center gap-2 border-slate-200 hover:bg-slate-50"
          data-testid="btn-exportar-excel"
        >
          <Download className="w-4 h-4" />
          Exportar Excel
        </Button>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => navegarMes(-1)} className="hover:bg-slate-100" data-testid="btn-mes-anterior">
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </Button>

            <CardTitle className="text-xl font-semibold text-slate-800" data-testid="titulo-mes">
              {MESES[mesAtual]} {anoAtual}
            </CardTitle>

            <Button variant="ghost" size="icon" onClick={() => navegarMes(1)} className="hover:bg-slate-100" data-testid="btn-mes-proximo">
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="pt-4">
          {loading ? (
            <LoadingSpinner message="Carregando agenda..." />
          ) : (
            <>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DIAS_SEMANA.map((dia) => (
                  <div key={dia} className="text-center text-xs font-semibold text-slate-500 py-2 uppercase tracking-wider">
                    {dia}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1" data-testid="calendario-grid">
                {diasDoMes.map((item, index) => (
                  <DiaCelula
                    key={item.data || `vazio-${index}`}
                    item={item}
                    index={index}
                    agendamentosDia={getAgendamentosDoDia(item.data)}
                    hoje={isHoje(item.data)}
                    abrirDia={abrirDia}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-slate-900"></div>
          <span>Hoje</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-slate-100"></div>
          <span>Com agendamentos</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border border-slate-200 bg-white"></div>
          <span>Disponível</span>
        </div>
      </div>
    </div>
  );
}
